import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ShieldOff, AlertTriangle } from "lucide-react";
import { SidebarContent } from "@/components/layout/Sidebar";
import { Topbar } from "@/components/layout/Topbar";
import { useAuth } from "@/context/AuthContext";
import { isPiiRoute, isSaasHiddenRoute, isEntidadBloqueada } from "@/lib/rbac";
import { areaForRoute, planFeatureForRoute, girosForRoute,
         ROL_CARGADOR_LISTAS, RUTAS_CARGADOR_LISTAS } from "@/lib/nav";
import { esActividadVulnerable } from "@/lib/actividad_vulnerable";
import OnboardingPending from "@/pages/OnboardingPending";
import MembresiaPago from "@/pages/MembresiaPago";

export function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PlatformRestricted() {
  return (
    <div data-testid="pii-restricted" className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <ShieldOff className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
      <h2 className="font-heading text-lg font-semibold text-slate-700">Acceso restringido</h2>
      <p className="max-w-md text-sm text-slate-500">
        FELVORA opera como plataforma pura. La cuenta de administración del SaaS no tiene acceso
        a datos personales (PII) de los clientes de las entidades. Esta vista solo está disponible
        para los usuarios de la entidad.
      </p>
    </div>
  );
}

function AreaRestricted() {
  return (
    <div data-testid="area-restricted" className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <ShieldOff className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
      <h2 className="font-heading text-lg font-semibold text-slate-700">Área no autorizada</h2>
      <p className="max-w-md text-sm text-slate-500">
        Tu perfil no tiene acceso a este módulo. El administrador de tu entidad asigna las áreas
        de trabajo de cada usuario. Solicítale acceso si lo necesitas para tu función.
      </p>
    </div>
  );
}

// ago-2026 · Fase 1 — candado por VERTICAL (giro). Ver `girosForRoute` en lib/nav.js.
function GiroRestricted() {
  return (
    <div data-testid="giro-restricted" className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <ShieldOff className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
      <h2 className="font-heading text-lg font-semibold text-slate-700">Módulo de otro régimen</h2>
      <p className="max-w-md text-sm text-slate-500">
        Este módulo pertenece a un régimen regulatorio distinto al de tu entidad y no forma parte
        de tus obligaciones. Usa el menú lateral: ahí solo aparecen los módulos que te aplican.
      </p>
    </div>
  );
}

function PlanRestricted() {  return (
    <div data-testid="plan-restricted" className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <ShieldOff className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
      <h2 className="font-heading text-lg font-semibold text-slate-700">Módulo no incluido en tu plan</h2>
      <p className="max-w-md text-sm text-slate-500">
        Este módulo no está disponible en tu plan actual. Para habilitarlo, mejora tu plan y
        contáctanos por WhatsApp; con gusto te ayudamos.
      </p>
      <a
        data-testid="plan-upgrade-whatsapp"
        href="https://wa.me/522224860318?text=Hola,%20quiero%20mejorar%20mi%20plan%20para%20habilitar%20un%20m%C3%B3dulo%20adicional."
        target="_blank"
        rel="noreferrer"
        className="mt-1 inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
      >
        Mejorar mi plan por WhatsApp
      </a>
    </div>
  );
}

function PermsError() {
  return (
    <div data-testid="perms-error" className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center">
      <AlertTriangle className="h-10 w-10 text-amber-500" strokeWidth={1.5} />
      <h2 className="font-heading text-lg font-semibold text-slate-700">
        No pudimos cargar tus permisos
      </h2>
      <p className="max-w-md text-sm text-slate-500">
        La plataforma no respondió al consultar tu perfil de acceso, así que no podemos abrir
        este módulo con seguridad. Vuelve a intentarlo; si sigue igual, cierra sesión y entra
        de nuevo.
      </p>
      <button
        data-testid="perms-error-retry"
        onClick={() => window.location.reload()}
        className="mt-1 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:opacity-90"
      >
        Volver a intentar
      </button>
    </div>
  );
}

export function AppLayout() {
  const { user, perms, moduloBloqueado, giro } = useAuth();
  const location = useLocation();
  // ago-2026 — corte al spinner infinito de permisos. `perms` queda en null tanto si
  // /rbac/me está en vuelo como si FALLÓ (AuthContext hace setPerms(null) en el catch),
  // así que sin un límite de tiempo la pantalla giraba para siempre. A los 8 s dejamos de
  // esperar y mostramos un estado accionable.
  const [permsTimeout, setPermsTimeout] = useState(false);
  useEffect(() => {
    if (perms) { setPermsTimeout(false); return undefined; }
    const t = setTimeout(() => setPermsTimeout(true), 8000);
    return () => clearTimeout(t);
  }, [perms]);

  // Entidad pendiente de pago de membresía (alta nueva): confina a la pantalla de pago.
  if (user?.role !== "super_admin" && user?.entity_estatus === "pendiente_pago") {
    return (
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <Topbar />
        <main className="flex-1 overflow-y-auto"><MembresiaPago /></main>
      </div>
    );
  }

  // Entidad pendiente/rechazada: sesión confinada a la pantalla de onboarding.
  if (user?.role !== "super_admin" && isEntidadBloqueada(user?.entity_estatus)) {
    return (
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <Topbar />
        <main className="flex-1 overflow-y-auto"><OnboardingPending /></main>
      </div>
    );
  }

  // Fase 3 — Restricción por impago: estado de pago de la entidad.
  const accesoPago = user?.role !== "super_admin" ? user?.entity_acceso_pago : "ok";
  const restringida = accesoPago === "restringida";
  const enGracia = accesoPago === "gracia";
  const graciaFecha = user?.entity_pago_gracia_limite
    ? new Date(user.entity_pago_gracia_limite).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  // Entidad restringida: confina a Facturación (única pantalla para regularizar el pago).
  if (restringida && location.pathname !== "/facturacion") {
    return <Navigate to="/facturacion" replace />;
  }

  const PagoBanner = (restringida || enGracia) ? (
    <div data-testid="pago-banner"
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium ${restringida ? "bg-red-600 text-white" : "bg-amber-100 text-amber-800"}`}>
      <AlertTriangle className="h-4 w-4 shrink-0" />
      {restringida
        ? "Suscripción suspendida por falta de pago. Regulariza tu pago en esta pantalla para reactivar el acceso a todo el sistema."
        : `Tu último pago no se completó. Regulariza tu suscripción antes del ${graciaFecha || "vencimiento"} para no perder el acceso.`}
    </div>
  ) : null;

  const blocked = user?.role === "super_admin" && (isPiiRoute(location.pathname) || isSaasHiddenRoute(location.pathname));

  // Candado por ÁREA del menú (usuarios de entidad): si la ruta pertenece a un área
  // no asignada al usuario, se bloquea aunque conozca la URL. admin_entidad nunca se bloquea.
  const area = areaForRoute(location.pathname);
  // Mientras cargan los permisos (perms === null) en una ruta de área no-principal, esperamos
  // antes de montar la página: evita disparar llamadas que el backend rechaza con 403
  // (área restringida) y el consiguiente overlay de error en modo dev. Cierre cosmético.
  const sinPerms =
    !perms &&
    !!user &&
    user.role !== "super_admin" &&
    !!area &&
    area !== "principal";
  const awaitingPerms = sinPerms && !permsTimeout;
  const permsFailed = sinPerms && permsTimeout;
  // sep-2026 · El «Cargador de Listas» entra a SUS dos pantallas por ROL, no por área (su
  // área fija es `principal`). Cualquier otra ruta sigue cayendo en «Área no autorizada».
  const rutaDeListas =
    user?.role === ROL_CARGADOR_LISTAS && RUTAS_CARGADOR_LISTAS.includes(location.pathname);
  const areaBlocked =
    !!perms &&
    user?.role !== "super_admin" &&
    !perms.es_admin_total &&
    !!area &&
    area !== "principal" &&
    !rutaDeListas &&
    !(perms.areas_acceso || []).includes(area);

  // ago-2026 · Fase 1 — Candado por VERTICAL. A diferencia del candado por área, este SÍ
  // aplica a `admin_entidad` / `es_admin_total`: el giro es una frontera regulatoria, no un
  // permiso que el administrador de la entidad pueda darse a sí mismo. `super_admin` (la
  // plataforma) queda fuera. Contraparte en UI del middleware `guard_sofom_por_giro.py`.
  const girosRuta = girosForRoute(location.pathname);
  const giroBlocked =
    !!perms &&               // `giro` sale de perms; sin ellos aún no se decide
    !!user &&
    user.role !== "super_admin" &&
    !!girosRuta &&
    !girosRuta.includes(giro);

  // Candado por PLAN: la ruta pertenece a un módulo NO incluido en el plan contratado.
  // Aplica a toda la entidad (incl. admin_entidad); super_admin nunca se restringe.
  const planBlocked =
    !!perms &&
    user?.role !== "super_admin" &&
    moduloBloqueado(planFeatureForRoute(location.pathname));

  // ago-2026 · Bloque A — el tablero SOFOM (/dashboard) es a donde llegan el login, la raíz
  // y el catch-all. Para una Actividad Vulnerable eso mostraba "Módulo de otro régimen":
  // ahora se manda a SU pantalla de inicio en vez de a una pantalla vacía.
  if (giroBlocked && location.pathname === "/dashboard" && esActividadVulnerable(giro))
    return <Navigate to="/inmobiliaria/inicio" replace />;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <aside className="hidden w-72 shrink-0 lg:block">
        <SidebarContent />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar />
        {PagoBanner}
        <main className="flex-1 overflow-y-auto">
          {blocked ? <PlatformRestricted /> : awaitingPerms ? (
            <div className="flex h-full items-center justify-center" data-testid="perms-loading">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-primary" />
            </div>
          ) : permsFailed ? <PermsError />
            : giroBlocked ? <GiroRestricted />
            : planBlocked ? <PlanRestricted /> : areaBlocked ? <AreaRestricted /> : <Outlet />}
        </main>
      </div>
    </div>
  );
}
